import { useEffect, useId, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { CheckCircle2, Flag, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { reportProblem } from "@/lib/api";
import { PROBLEM_CATEGORIES, PROBLEM_DESCRIPTION_MAX } from "@/lib/problemReport";
import { cn } from "@/lib/utils";

/**
 * Candidate "Report a problem" control (MVP 3.0). Renders a small, unobtrusive
 * text link; clicking it opens a compact modal (preset categories + optional
 * free text) cloning the shared `ConfirmDialog` overlay. Self-contained — drop
 * `<ReportProblemTrigger token={token} attemptId={attemptId} />` anywhere in the
 * candidate flow (Review / Section-Review footers, Done screen).
 *
 * Non-blocking by design: a report is a separate request that never touches the
 * attempt's answers or submission. Renders nothing without an attempt id, so the
 * placements don't have to null-check.
 */
export function ReportProblemTrigger({
  token,
  attemptId,
  className,
}: {
  token: string;
  attemptId: string | null;
  className?: string;
}) {
  const [open, setOpen] = useState(false);

  if (!attemptId) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          "inline-flex items-center gap-1.5 rounded text-xs text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
          className,
        )}
      >
        <Flag className="h-3.5 w-3.5" aria-hidden="true" />
        Report a problem
      </button>
      {open && (
        <ReportProblemDialog
          token={token}
          attemptId={attemptId}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function ReportProblemDialog({
  token,
  attemptId,
  onClose,
}: {
  token: string;
  attemptId: string;
  onClose: () => void;
}) {
  const titleId = useId();
  const descId = useId();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [description, setDescription] = useState("");
  const [done, setDone] = useState(false);

  const mutation = useMutation({
    mutationFn: () =>
      reportProblem(token, attemptId, {
        categories: [...selected],
        description: description.trim() || null,
      }),
    onSuccess: () => setDone(true),
  });

  // Escape closes the modal — matches ConfirmDialog. Disabled mid-send so a
  // stray keypress can't abandon an in-flight report.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !mutation.isPending) onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose, mutation.isPending]);

  const hasInput = selected.size > 0 || description.trim().length > 0;
  const canSubmit = hasInput && !mutation.isPending;

  function toggle(key: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={() => !mutation.isPending && onClose()}
    >
      <div
        className="max-h-[calc(100vh-2rem)] w-full max-w-md overflow-y-auto rounded-lg border border-border bg-card p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        {done ? (
          <div className="space-y-4 text-center">
            <CheckCircle2
              className="mx-auto h-10 w-10 text-success"
              aria-hidden="true"
            />
            <div className="space-y-1">
              <h2
                id={titleId}
                className="text-lg font-semibold tracking-tight"
              >
                Thanks — your report was sent
              </h2>
              <p className="text-sm text-muted-foreground">
                The recruiter will see it alongside your results. Your answers
                are unchanged — you can keep going.
              </p>
            </div>
            <Button
              type="button"
              onClick={onClose}
              className="w-full cursor-pointer"
            >
              Close
            </Button>
          </div>
        ) : (
          <>
            <h2 id={titleId} className="text-lg font-semibold tracking-tight">
              Report a problem
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Let the recruiter know if something went wrong. This won't change
              or submit your answers.
            </p>

            <fieldset
              className="mt-4 space-y-2"
              disabled={mutation.isPending}
            >
              <legend className="sr-only">What went wrong?</legend>
              {PROBLEM_CATEGORIES.map((c) => (
                <label
                  key={c.key}
                  className="flex cursor-pointer items-start gap-2.5 rounded-md border border-border bg-background p-2.5 text-sm transition-colors hover:bg-muted/40"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(c.key)}
                    onChange={() => toggle(c.key)}
                    className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-border accent-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  />
                  <span className="text-foreground">{c.label}</span>
                </label>
              ))}
            </fieldset>

            <div className="mt-3 space-y-1">
              <label
                htmlFor={descId}
                className="text-xs font-medium text-muted-foreground"
              >
                Describe it (optional)
              </label>
              <textarea
                id={descId}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={PROBLEM_DESCRIPTION_MAX}
                disabled={mutation.isPending}
                rows={3}
                placeholder="What happened?"
                className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>

            {mutation.isError && (
              <div
                role="alert"
                className="mt-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                {errorMessage(mutation.error)}
              </div>
            )}

            <div className="mt-5 flex items-center justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={mutation.isPending}
                className="cursor-pointer"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => mutation.mutate()}
                disabled={!canSubmit}
                className="cursor-pointer"
              >
                {mutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    Sending…
                  </>
                ) : (
                  "Send report"
                )}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function errorMessage(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  if (msg.includes("429")) {
    return "Too many reports just now — please wait a moment and try again.";
  }
  return "We couldn't send your report. Please try again.";
}
